import { LABELS } from "../../../../constants";
import DynamicIntentDisplay from "../../../shared/DynamicIntentDisplay";
import styles from "./StepMetaInfoRunInfo.module.scss";

/** @returns {null} not bound, {''} empty pill, else pill text */
function bindingPillText(row) {
    const ph = row.placeholder?.trim();
    if (!ph) return null;
    return row.valueDisplay ?? ph;
}

/**
 * Read-only "run info" panel for step meta (dynamic intent, bindings, intent tokens).
 * Presentational only — parent supplies parsed props.
 *
 * @param {object} props
 * @param {string} props.intentType
 * @param {string} props.intentText
 * @param {{ rowKey: string, placeholder: string, bindingType: string, valueDisplay?: string }[]} props.bindingRows
 * @param {string[]} props.intentTokens
 * @param {string} [props.className]
 */
export default function StepMetaInfoRunInfo({
    intentType,
    intentText,
    bindingRows = [],
    intentTokens = [],
    className = "",
}) {
    const bindingCount = bindingRows.length;
    const tokenCount = intentTokens.length;
    const paramCountLabel =
        bindingCount === 1 ? LABELS.META_COUNT_PARAM_SINGULAR : LABELS.META_COUNT_PARAM_PLURAL;
    const tokenCountLabel =
        tokenCount === 1 ? LABELS.META_COUNT_TOKEN_SINGULAR : LABELS.META_COUNT_TOKEN_PLURAL;

    return (
        <div className={`${styles.root} ${className}`.trim()}>
            <DynamicIntentDisplay
                intentType={intentType}
                intentText={intentText}
                headingId="meta-heading-dynamic-intent"
            />

            <section className={styles.section} aria-labelledby="meta-heading-bindings">
                <div className={styles.sectionCard}>
                    <div className={styles.sectionHeader}>
                        <h3 id="meta-heading-bindings" className={styles.sectionHeading}>
                            {LABELS.META_SECTION_PARAMETER_BINDINGS}
                        </h3>
                        {bindingCount > 0 ? (
                            <span className={styles.countBadge} aria-label={`${bindingCount} ${paramCountLabel}`}>
                                {bindingCount} {paramCountLabel}
                            </span>
                        ) : null}
                    </div>
                    {bindingCount === 0 ? (
                        <p className={styles.empty}>{LABELS.META_EMPTY_BINDINGS}</p>
                    ) : (
                        <div className={`${styles.table} ${styles.tableBindings}`} role="table" aria-label={LABELS.META_SECTION_PARAMETER_BINDINGS}>
                            <div className={styles.trHead} role="row">
                                <span className={styles.th} role="columnheader">{LABELS.META_COL_KEY}</span>
                                <span className={styles.th} role="columnheader">{LABELS.META_COL_VALUE}</span>
                                <span className={styles.th} role="columnheader">{LABELS.META_COL_TYPE}</span>
                            </div>
                            {bindingRows.map((row) => {
                                const statusLabel = row.bindingType
                                    ? row.bindingType.trim().toUpperCase()
                                    : "";
                                const pillText = bindingPillText(row);
                                return (
                                    <div className={styles.tr} role="row" key={row.rowKey}>
                                        <span className={styles.tdKey} role="cell">{row.rowKey}</span>
                                        <span className={styles.tdValue} role="cell">
                                            {pillText == null ? (
                                                <em className={styles.notBound}>{LABELS.META_NOT_BOUND}</em>
                                            ) : pillText ? (
                                                <code className={styles.codePill}>{pillText}</code>
                                            ) : null}
                                        </span>
                                        <span className={styles.tdStatus} role="cell">
                                            {row.bindingType ? (
                                                <span className={styles.metaCategoryPill}>
                                                    {statusLabel}
                                                </span>
                                            ) : (
                                                <span className={styles.chipMuted}>—</span>
                                            )}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>

            <section className={styles.section} aria-labelledby="meta-heading-tokens">
                <div className={styles.sectionCard}>
                    <div className={styles.sectionHeader}>
                        <h3 id="meta-heading-tokens" className={styles.sectionHeading}>
                            {LABELS.META_SECTION_INTENT_TOKENS}
                        </h3>
                        {tokenCount > 0 ? (
                            <span className={styles.countBadge} aria-label={`${tokenCount} ${tokenCountLabel}`}>
                                {tokenCount} {tokenCountLabel}
                            </span>
                        ) : null}
                    </div>
                    {tokenCount === 0 ? (
                        <p className={styles.empty}>{LABELS.META_EMPTY_TOKENS}</p>
                    ) : (
                        <div className={styles.tokenWell}>
                            <ul className={styles.tokenList}>
                                {intentTokens.map((t) => (
                                    <li key={t}>
                                        <span className={styles.chipToken}>{t}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
